import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X,
  ChevronRight,
  Shield,
  Clock,
  Award,
  Users,
  CheckCircle2,
  Star,
  Droplets,
  Hammer,
  Paintbrush,
  Zap,
  Truck,
  Heart,
  Scissors,
  Sparkles,
  Home,
  Wrench,
  Building,
  MessageSquare
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Industry configurations
const INDUSTRY_CONFIG = {
  mark: {
    name: "Markentreprenad",
    icon: Droplets,
    heroImage: "https://images.unsplash.com/photo-1759950345011-ee5a96640e00?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Dränering", desc: "Skydda din fastighet från fuktskador med professionell dränering.", icon: Droplets },
      { title: "Schaktning", desc: "Effektiv schaktning för alla typer av projekt.", icon: Hammer },
      { title: "Grundarbete", desc: "Stabil grund för nybyggnation och tillbyggnad.", icon: Building },
      { title: "Dagvattenlösningar", desc: "Hållbar hantering av dagvatten på din tomt.", icon: Droplets }
    ],
    aboutText: "Med lång erfarenhet inom markentreprenad levererar vi kvalitetsarbeten för både privatpersoner och företag.",
    keywords: ["markarbeten", "dränering", "schakt"]
  },
  glas: {
    name: "Glasmästeri",
    icon: Building,
    heroImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Balkonginglasning", desc: "Förläng säsongen med en inglasad balkong.", icon: Building },
      { title: "Uterum", desc: "Skapa ett extra rum med ett modernt uterum.", icon: Home },
      { title: "Glasmontering", desc: "Professionell montering av glas i alla former.", icon: Wrench },
      { title: "Räckeslösningar", desc: "Eleganta glasräcken för balkong och trappa.", icon: Building }
    ],
    aboutText: "Vi är specialister på glaslösningar som förhöjer både funktion och estetik i din bostad.",
    keywords: ["glas", "inglasning", "uterum"]
  },
  malare: {
    name: "Måleri",
    icon: Paintbrush,
    heroImage: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Invändig målning", desc: "Ge ditt hem nytt liv med professionell invändig målning.", icon: Paintbrush },
      { title: "Utvändig målning", desc: "Skydda och försköna din fastighet utvändigt.", icon: Home },
      { title: "Tapetsering", desc: "Fackmannamässig tapetsering för alla stilar.", icon: Paintbrush },
      { title: "Fasadunderhåll", desc: "Långsiktigt skydd och underhåll av fasaden.", icon: Building }
    ],
    aboutText: "Vi är målarfirman som kombinerar hantverk med modern teknik för ett resultat som håller.",
    keywords: ["målning", "tapetsering", "fasad"]
  },
  bygg: {
    name: "Bygg & Renovering",
    icon: Hammer,
    heroImage: "https://images.unsplash.com/photo-1759150712360-6d48015e4f86?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Renovering", desc: "Totalrenoveringar och delrenoveringar av hög kvalitet.", icon: Hammer },
      { title: "Tillbyggnad", desc: "Bygg ut ditt hem med en professionell tillbyggnad.", icon: Building },
      { title: "Snickeri", desc: "Skräddarsydda snickerilösningar för ditt projekt.", icon: Wrench },
      { title: "Byggservice", desc: "Snabb och pålitlig byggservice för alla behov.", icon: Hammer }
    ],
    aboutText: "Vi bygger, renoverar och förbättrar – alltid med kvalitet och kundens bästa i fokus.",
    keywords: ["bygg", "renovering", "snickeri"]
  },
  elektriker: {
    name: "Elektriker",
    icon: Zap,
    heroImage: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Elinstallation", desc: "Säkra elinstallationer för hem och företag.", icon: Zap },
      { title: "Service", desc: "Regelbunden service för trygg el i fastigheten.", icon: Wrench },
      { title: "Felsökning", desc: "Snabb och effektiv felsökning av elproblem.", icon: Zap },
      { title: "Belysning", desc: "Moderna belysningslösningar för alla miljöer.", icon: Sparkles }
    ],
    aboutText: "Certifierade elektriker som levererar säkra och hållbara ellösningar.",
    keywords: ["el", "elektriker", "installation"]
  },
  flytt: {
    name: "Flytt & Transport",
    icon: Truck,
    heroImage: "https://images.pexels.com/photos/7464702/pexels-photo-7464702.jpeg?auto=compress&cs=tinysrgb&w=2070",
    services: [
      { title: "Bohagsflytt", desc: "Trygg och smidig flytt av hela ditt bohag.", icon: Truck },
      { title: "Företagsflytt", desc: "Effektiv kontorsflytt med minimal störning.", icon: Building },
      { title: "Bärhjälp", desc: "Extra händer när du behöver hjälp att bära.", icon: Users },
      { title: "Packning och transport", desc: "Vi packar, transporterar och packar upp.", icon: Truck }
    ],
    aboutText: "Vi gör din flytt enkel och stressfri – från packning till uppackning.",
    keywords: ["flytt", "transport", "bärhjälp"]
  },
  veterinar: {
    name: "Veterinär",
    icon: Heart,
    heroImage: "https://images.unsplash.com/photo-1628009368231-7bb7cfcb0def?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Hälsokontroller", desc: "Regelbundna kontroller för ditt djurs välmående.", icon: Heart },
      { title: "Vaccination", desc: "Skydda ditt husdjur med rätt vaccinationer.", icon: Shield },
      { title: "Behandling", desc: "Professionell behandling av sjukdomar och skador.", icon: Heart },
      { title: "Akut vård", desc: "Snabb hjälp när ditt djur behöver akut vård.", icon: Clock }
    ],
    aboutText: "Vi tar hand om dina fyrbenta familjemedlemmar med kärlek och expertis.",
    keywords: ["veterinär", "djur", "husdjur"]
  },
  frisor: {
    name: "Frisör",
    icon: Scissors,
    heroImage: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Klippning", desc: "Professionell klippning för alla stilar.", icon: Scissors },
      { title: "Färgning", desc: "Färgning och slingor som lyfter din look.", icon: Paintbrush },
      { title: "Styling", desc: "Styling för vardag och fest.", icon: Sparkles },
      { title: "Skäggtrim", desc: "Välvårdad skäggtrim för herrar.", icon: Scissors }
    ],
    aboutText: "Vi skapar frisyrer som passar din stil och personlighet.",
    keywords: ["frisör", "salong", "hår"]
  },
  stad: {
    name: "Städ",
    icon: Sparkles,
    heroImage: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Hemstädning", desc: "Regelbunden hemstädning som ger dig mer tid.", icon: Home },
      { title: "Företagsstädning", desc: "Professionell städning av kontor och lokaler.", icon: Building },
      { title: "Flyttstädning", desc: "Grundlig städning vid flytt – garanti ingår.", icon: Sparkles },
      { title: "Fönsterputs", desc: "Kristallklara fönster året runt.", icon: Sparkles }
    ],
    aboutText: "Vi levererar städtjänster som gör skillnad – för hem och företag.",
    keywords: ["städning", "hemstädning", "lokalvård"]
  },
  plattsattning: {
    name: "Plattsättning",
    icon: Building,
    heroImage: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Kakel & Klinker", desc: "Professionell plattsättning i badrum och kök.", icon: Building },
      { title: "Natursten", desc: "Vacker natursten för golv och väggar.", icon: Building },
      { title: "Mosaik", desc: "Kreativa mosaikarbeten för unika ytor.", icon: Sparkles },
      { title: "Poolplattsättning", desc: "Hållbar plattsättning för poolområden.", icon: Droplets }
    ],
    aboutText: "Vi är specialister på plattsättning med fokus på kvalitet och hållbarhet.",
    keywords: ["plattsättning", "kakel", "klinker"]
  },
  vvs: {
    name: "VVS",
    icon: Wrench,
    heroImage: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Rörmokeri", desc: "Installation och reparation av rör och avlopp.", icon: Wrench },
      { title: "Värmesystem", desc: "Installation av värmepumpar och radiatorer.", icon: Home },
      { title: "Badrumsrenovering", desc: "Komplett VVS-arbete vid badrumsrenovering.", icon: Droplets },
      { title: "Akut VVS", desc: "Snabb hjälp vid läckor och stopp.", icon: Clock }
    ],
    aboutText: "Vi erbjuder kompletta VVS-lösningar för hem och företag.",
    keywords: ["vvs", "rörmokare", "rör"]
  },
  tak: {
    name: "Takläggning",
    icon: Home,
    heroImage: "https://images.unsplash.com/photo-1632759145351-1d592919f522?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Takbyte", desc: "Komplett byte av tak med kvalitetsmaterial.", icon: Home },
      { title: "Takrenovering", desc: "Renovering och underhåll av befintligt tak.", icon: Wrench },
      { title: "Takpannor", desc: "Installation av betong- och lertakpannor.", icon: Building },
      { title: "Plåttak", desc: "Moderna plåttak med lång livslängd.", icon: Shield }
    ],
    aboutText: "Vi lägger tak som håller i generationer – med garanti på alla arbeten.",
    keywords: ["tak", "takläggare", "takbyte"]
  },
  tradgard: {
    name: "Trädgård",
    icon: Home,
    heroImage: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Trädgårdsdesign", desc: "Skräddarsydd design för din drömträdgård.", icon: Home },
      { title: "Gräsklippning", desc: "Regelbunden skötsel av gräsmatta.", icon: Home },
      { title: "Beskärning", desc: "Professionell beskärning av träd och buskar.", icon: Scissors },
      { title: "Plantering", desc: "Plantering av blommor, buskar och träd.", icon: Sparkles }
    ],
    aboutText: "Vi skapar och sköter trädgårdar som blomstrar året runt.",
    keywords: ["trädgård", "trädgårdsarbete", "gräsklippning"]
  },
  snickare: {
    name: "Snickeri",
    icon: Hammer,
    heroImage: "https://images.unsplash.com/photo-1504148455328-c376907d081c?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Inredningssnickeri", desc: "Skräddarsydda inredningslösningar.", icon: Home },
      { title: "Altan & Trall", desc: "Bygg av altaner och trallgolv.", icon: Building },
      { title: "Köksrenovering", desc: "Montering och anpassning av kök.", icon: Wrench },
      { title: "Möbelsnickeri", desc: "Unika möbler efter dina önskemål.", icon: Hammer }
    ],
    aboutText: "Hantverk i trä med passion och precision – det är vår specialitet.",
    keywords: ["snickare", "snickeri", "träarbete"]
  },
  default: {
    name: "Tjänster",
    icon: Wrench,
    heroImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2070&auto=format&fit=crop",
    services: [
      { title: "Konsultation", desc: "Professionell rådgivning för dina behov.", icon: MessageSquare },
      { title: "Projektledning", desc: "Vi leder ditt projekt från start till mål.", icon: Award },
      { title: "Utförande", desc: "Kvalitetsarbete med fokus på resultat.", icon: Wrench },
      { title: "Efterservice", desc: "Vi finns här även efter avslutat projekt.", icon: Shield }
    ],
    aboutText: "Vi levererar professionella tjänster med fokus på kvalitet och kundnöjdhet.",
    keywords: ["tjänster", "företag"]
  }
};

// Company abbreviations that should remain uppercase
const COMPANY_ABBREVIATIONS = ["AB", "HB", "KB", "AS", "OY", "APS", "LTD", "LLC", "GMBH"];

// Swedish character mappings for URL slugs
const SWEDISH_CHAR_MAP = {
  'a': 'ä', // Context-dependent, handled separately
  'o': 'ö', // Context-dependent, handled separately
};

// Common Swedish word patterns that should have special characters
const SWEDISH_WORD_PATTERNS = {
  'angamarkens': 'Ängamarkens',
  'angamarken': 'Ängamarken',
  'hollviken': 'Höllviken',
  'malmo': 'Malmö',
  'goteborg': 'Göteborg',
  'jonkoping': 'Jönköping',
  'linkoping': 'Linköping',
  'norrkoping': 'Norrköping',
  'vaxjo': 'Växjö',
  'orebro': 'Örebro',
  'vasteras': 'Västerås',
  'sodermanland': 'Södermanland',
  'sormland': 'Sörmland',
  'ostergotland': 'Östergötland',
  'skaraborg': 'Skaraborg',
  'halsingland': 'Hälsingland',
  'jamtland': 'Jämtland',
  'angermanland': 'Ångermanland',
  'gavle': 'Gävle',
  'sundsvall': 'Sundsvall',
  'umea': 'Umeå',
  'ostersund': 'Östersund',
  'boras': 'Borås',
  'nassjo': 'Nässjö',
  'tranas': 'Tranås',
  'eksjo': 'Eksjö',
  'marsta': 'Märsta',
  'taby': 'Täby',
  'jarfalla': 'Järfälla',
  'sodertalje': 'Södertälje',
  'nykoping': 'Nyköping',
  'skane': 'Skåne',
  'osteraker': 'Österåker',
  'nassjo': 'Nässjö',
};

// Helper function to format company name
const formatCompanyName = (slug) => {
  if (!slug) return "Ert Företag";
  
  return slug
    .split("-")
    .map(word => {
      // Check if it's a known abbreviation (case-insensitive)
      const upperWord = word.toUpperCase();
      if (COMPANY_ABBREVIATIONS.includes(upperWord)) {
        return upperWord;
      }
      
      // Check if it's a known Swedish word pattern
      const lowerWord = word.toLowerCase();
      if (SWEDISH_WORD_PATTERNS[lowerWord]) {
        return SWEDISH_WORD_PATTERNS[lowerWord];
      }
      
      // Default: Title case
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

// Helper function to format city name
const formatCityName = (slug) => {
  if (!slug) return null;
  
  const lowerSlug = slug.toLowerCase();
  
  // Check if it's a known Swedish city/place
  if (SWEDISH_WORD_PATTERNS[lowerSlug]) {
    return SWEDISH_WORD_PATTERNS[lowerSlug];
  }
  
  // Default: Title case
  return slug.charAt(0).toUpperCase() + slug.slice(1).toLowerCase();
};

// Helper function to format industry name for display
const formatIndustryDisplay = (industry, config) => {
  if (!industry || industry === "default") return null;
  
  // Map industry slugs to display names
  const industryDisplayNames = {
    'bygg': 'Byggfirma',
    'mark': 'Markentreprenad',
    'glas': 'Glasmästeri',
    'malare': 'Målerifirma',
    'elektriker': 'Elektriker',
    'flytt': 'Flyttfirma',
    'veterinar': 'Veterinärklinik',
    'frisor': 'Frisörsalong',
    'stad': 'Städfirma',
    'plattsattning': 'Plattsättning',
    'vvs': 'VVS-firma',
    'tak': 'Takläggare',
    'tradgard': 'Trädgårdsservice',
    'snickare': 'Snickeri',
    'ror': 'Rörmokare',
  };
  
  return industryDisplayNames[industry] || config?.name || null;
};

// Why choose us points
const WHY_CHOOSE_US = [
  { title: "Erfarna proffs", desc: "Lång erfarenhet och expertis inom branschen" },
  { title: "Kostnadsfri offert", desc: "Vi lämnar alltid en tydlig offert utan förpliktelser" },
  { title: "Kvalitetsgaranti", desc: "Vi står för kvalitet i allt vi gör" },
  { title: "Lokal närvaro", desc: "Vi finns nära dig och känner till lokala förhållanden" }
];

export const DemoPage = () => {
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  // Get URL parameters
  const company = searchParams.get("company");
  const industry = searchParams.get("industry") || "default";
  const city = searchParams.get("city");
  const customHeadline = searchParams.get("headline");
  const customSubheadline = searchParams.get("subheadline");
  const rating = searchParams.get("rating");
  const reviews = searchParams.get("reviews");

  // Get industry config
  const config = INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG.default;
  const companyName = formatCompanyName(company);
  const cityName = formatCityName(city);
  const industryDisplay = formatIndustryDisplay(industry, config);
  const IndustryIcon = config.icon;
  
  // Check if we should show ratings
  const showRatings = rating && reviews;

  useEffect(() => {
    document.title = `${companyName} | Demo`;
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [companyName]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Tack för din förfrågan!", {
      description: "Detta är en demosida – kontakta oss för att skapa din egen."
    });
    setFormData({ name: "", email: "", phone: "", message: "" });
  };

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Generate headline - now shows company name prominently
  const headline = customHeadline || companyName;

  // Generate subheadline - shows industry and city
  const subheadline = customSubheadline || (
    industryDisplay && cityName
      ? `${industryDisplay} i ${cityName}`
      : industryDisplay
        ? industryDisplay
        : cityName
          ? `Professionella tjänster i ${cityName}`
          : "Professionella tjänster"
  );

  // Generate description text
  const descriptionText = cityName
    ? `${companyName} erbjuder kvalitetstjänster för privatpersoner och företag i ${cityName} med omnejd.`
    : `${companyName} erbjuder kvalitetstjänster för privatpersoner och företag.`;

  const navLinks = [
    { id: "tjanster", label: "Tjänster" },
    { id: "om-oss", label: "Om oss" },
    { id: "kontakt", label: "Kontakt" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="demo-page">
      {/* SEO: canonical and robots handled by GlobalSEOHead */}
      <Helmet>
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Toaster position="top-center" richColors />

      {/* Top Banner */}
      <div className="bg-[#2C3E36] text-white py-3 px-6 text-center text-sm font-['Inter']" data-testid="demo-banner">
        <span className="opacity-90">Denna demosida är framtagen för </span>
        <span className="font-semibold">{companyName}</span>
      </div>
      
      {/* Header */}
      <header 
        className={`sticky top-0 z-50 bg-white/97 backdrop-blur-md border-b border-gray-100 transition-all ${isScrolled ? "shadow-md" : ""}`}
        data-testid="demo-header"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a 
            href="#" 
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3"
            data-testid="demo-logo"
          >
            <div className="w-10 h-10 bg-[#3D7A6B] flex items-center justify-center rounded-lg">
              <IndustryIcon className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Barlow_Condensed'] font-bold text-lg text-[#2C3E36] tracking-tight">
              {companyName}
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="demo-nav">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="font-['Inter'] text-sm font-medium text-[#2C3E36] hover:text-[#3D7A6B] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => scrollToSection("kontakt")}
              className="demo-cta-button hidden sm:flex"
              data-testid="demo-header-cta"
            >
              Kontakta oss
            </button>
            
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white pt-24 px-6" data-testid="demo-mobile-menu">
          <button 
            className="absolute top-20 right-6 p-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
          {navLinks.map(link => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="block w-full text-left font-['Barlow_Condensed'] text-xl font-semibold py-4 border-b border-gray-100 text-[#2C3E36]"
            >
              {link.label}
            </button>
          ))}
          <button 
            onClick={() => scrollToSection("kontakt")}
            className="demo-cta-button mt-6 w-full justify-center"
          >
            Kontakta oss
          </button>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="demo-hero">
        <img 
          src={config.heroImage} 
          alt={config.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C3E36]/80 via-[#2C3E36]/60 to-[#2C3E36]/80" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-20">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <IndustryIcon className="w-4 h-4 text-[#E8A54B]" />
            <span className="text-white/90 text-sm font-['Inter']">{config.name}</span>
          </div>
          
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-testid="demo-headline">
            {headline}
          </h1>
          
          <p className="text-[#E8A54B] text-xl md:text-2xl font-['Barlow_Condensed'] font-semibold mb-6" data-testid="demo-subheadline">
            {subheadline}
          </p>
          
          {/* Rating display */}
          {showRatings && (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-testid="demo-rating">
              <Star className="w-5 h-5 text-[#E8A54B] fill-[#E8A54B]" />
              <span className="text-white font-semibold">{rating} / 5</span>
              <span className="text-white/70">baserat på {reviews} kundomdömen</span>
            </div>
          )}
          
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-10 font-['Inter']" data-testid="demo-description">
            {descriptionText}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <button 
              onClick={() => scrollToSection("kontakt")}
              className="demo-cta-button text-lg px-8 py-4"
              data-testid="demo-hero-cta"
            >
              Kontakta oss
              <ChevronRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scrollToSection("tjanster")}
              className="demo-cta-button-outline text-lg px-8 py-4"
            >
              Våra tjänster
            </button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#E8A54B]" />
              <span>Kostnadsfri offert</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#E8A54B]" />
              <span>Snabb återkoppling</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#E8A54B]" />
              <span>Kvalitetsgaranti</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="tjanster" className="py-20 md:py-28 bg-[#F8F7F4]" data-testid="demo-services">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#3D7A6B] font-['Inter'] text-sm font-semibold tracking-wide uppercase mb-4">
              Våra tjänster
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#2C3E36] mb-4">
              Så kan vi hjälpa dig
            </h2>
            <p className="text-[#5A6B62] text-lg max-w-xl mx-auto font-['Inter']">
              {cityName 
                ? `${companyName} erbjuder ett brett utbud av tjänster i ${cityName}`
                : `${companyName} erbjuder ett brett utbud av professionella tjänster`}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {config.services.map((service, i) => {
              const ServiceIcon = service.icon;
              return (
                <div 
                  key={service.title || `service-${i}`} 
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
                  data-testid={`demo-service-${i}`}
                >
                  <div className="w-14 h-14 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-4">
                    <ServiceIcon className="w-7 h-7 text-[#3D7A6B]" />
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-[#2C3E36] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-[#5A6B62] text-sm font-['Inter'] leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="om-oss" className="py-20 md:py-28 bg-white" data-testid="demo-about">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#3D7A6B] font-['Inter'] text-sm font-semibold tracking-wide uppercase mb-4">
                Om {companyName}
              </p>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#2C3E36] mb-6">
                {cityName 
                  ? `Din lokala partner i ${cityName}`
                  : "Din pålitliga partner"}
              </h2>
              <p className="text-[#5A6B62] text-lg leading-relaxed mb-6 font-['Inter']">
                {config.aboutText}
              </p>
              <p className="text-[#5A6B62] text-lg leading-relaxed mb-8 font-['Inter']">
                Vi värdesätter långsiktiga relationer och levererar alltid med kvalitet och omsorg. 
                Kontakta oss för en kostnadsfri konsultation.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Shield, text: "Försäkrad" },
                  { icon: Clock, text: "Snabb service" },
                  { icon: Award, text: "Kvalitetsgaranti" },
                  { icon: Users, text: "Personlig kontakt" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#3D7A6B]" />
                    </div>
                    <span className="text-[#4A5D52] font-medium text-sm font-['Inter']">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={config.heroImage}
                alt={companyName}
                className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#3D7A6B] text-white p-5 rounded-xl shadow-lg">
                {showRatings ? (
                  <>
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={`star-rating-${i}`} 
                          className={`w-4 h-4 ${i < Math.floor(parseFloat(rating)) ? 'text-[#E8A54B] fill-[#E8A54B]' : 'text-white/30'}`} 
                        />
                      ))}
                    </div>
                    <p className="font-['Inter'] text-sm font-semibold">{rating} / 5</p>
                    <p className="font-['Inter'] text-xs text-white/70">{reviews} omdömen</p>
                  </>
                ) : (
                  <>
                    <div className="flex gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={`star-default-${i}`} className="w-4 h-4 text-[#E8A54B] fill-[#E8A54B]" />
                      ))}
                    </div>
                    <p className="font-['Inter'] text-sm">Nöjda kunder</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-28 bg-[#2C3E36]" data-testid="demo-why-us">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#E8A54B] font-['Inter'] text-sm font-semibold tracking-wide uppercase mb-4">
              Varför välja oss
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white mb-4">
              Det som gör oss annorlunda
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_CHOOSE_US.map((item) => (
              <div 
                key={item.title} 
                className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10"
              >
                <CheckCircle2 className="w-8 h-8 text-[#E8A54B] mb-4" />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-white/70 text-sm font-['Inter']">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 md:py-28 bg-white" data-testid="demo-contact">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p className="text-[#3D7A6B] font-['Inter'] text-sm font-semibold tracking-wide uppercase mb-4">
                Kontakta {companyName}
              </p>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-[#2C3E36] mb-4">
                Få en kostnadsfri offert
              </h2>
              <p className="text-[#5A6B62] text-lg mb-10 font-['Inter']">
                Fyll i formuläret så återkommer vi inom 24 timmar. Du kan också ringa eller maila oss direkt.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#3D7A6B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A6B62] font-['Inter']">Telefon</p>
                    <span className="text-lg font-semibold text-[#2C3E36] font-['Inter']">
                      010-123 45 67
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#3D7A6B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#5A6B62] font-['Inter']">E-post</p>
                    <span className="text-lg font-semibold text-[#2C3E36] font-['Inter']">
                      info@{company || "foretag"}.se
                    </span>
                  </div>
                </div>

                {cityName && (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#3D7A6B]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5A6B62] font-['Inter']">Plats</p>
                      <span className="text-lg font-semibold text-[#2C3E36] font-['Inter']">
                        {cityName}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#F8F7F4] p-8 md:p-10 rounded-2xl">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="demo-contact-form">
                <div>
                  <label className="block text-sm font-semibold text-[#2C3E36] mb-2 font-['Inter']">
                    Ditt namn *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Vad heter du?"
                    className="demo-input"
                    data-testid="demo-form-name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#2C3E36] mb-2 font-['Inter']">
                      E-post
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="din@email.se"
                      className="demo-input"
                      data-testid="demo-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2C3E36] mb-2 font-['Inter']">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      required
                      placeholder="070-123 45 67"
                      className="demo-input"
                      data-testid="demo-form-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#2C3E36] mb-2 font-['Inter']">
                    Meddelande
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    placeholder="Berätta vad du behöver hjälp med..."
                    className="demo-input min-h-[120px]"
                    data-testid="demo-form-message"
                  />
                </div>

                <p className="text-xs text-gray-500">
                  Genom att fortsätta godkänner du vår{" "}
                  <a href="/integritetspolicy" className="text-[#255E69] hover:underline" target="_blank" rel="noopener noreferrer">
                    integritetspolicy
                  </a>.
                </p>

                <button 
                  type="submit" 
                  className="demo-cta-button w-full justify-center"
                  data-testid="demo-form-submit"
                >
                  Skicka förfrågan
                  <ChevronRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C3E36] text-white py-12" data-testid="demo-footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3D7A6B] flex items-center justify-center rounded-lg">
                <IndustryIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-['Barlow_Condensed'] font-bold text-xl tracking-tight">
                {companyName}
              </span>
            </div>
            
            <p className="text-white/60 text-sm font-['Inter'] text-center">
              Detta är en demosida framtagen av Elido.
            </p>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-8 text-center">
            <p className="text-white/40 text-sm font-['Inter']">
              © {new Date().getFullYear()} {companyName}. Demosida.
            </p>
          </div>
          
          {/* Elido CTA Section */}
          <div className="border-t border-white/10 mt-8 pt-8 text-center">
            <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold text-white mb-3">
              Vill ni ha en liknande hemsida?
            </h3>
            <p className="text-white/60 text-base mb-6 font-['Inter']">
              Vi hjälper företag att skapa professionella hemsidor som konverterar besökare till kunder.
            </p>
            <a 
              href="https://elido.nu" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8A54B] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#d4943f] transition-colors"
            >
              Kontakta Elido AB
              <ChevronRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;
